import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, CircularProgress, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import { useTranslation } from 'react-i18next';
import OutlookStyleEmailView from 'src/components/Email2Case/OutlookStyleEmailView';
import {
  getConversationsByExternalLink,
  getMailOrbitErrorKey
} from 'src/utils/MailOrbitService';
import useStyles from './useStyles';
import ConversationView from './ConversationView';
import BackToList from './BackToList';

// MailOrbit tarafındaki genel "external system" anahtarı (harici link eşlemesi SR-004/SR-005 ile aynı).
const EXTERNAL_SYSTEM = 'sirius';

/**
 * WorkOrder "E-mail" sekmesi (MailOrbit modu). İş emrine bağlı conversation by-external-link ile bulunur.
 * Telefon logları sekmesi mevcut OmniChannel verisiyle, OutlookStyleEmailView'in phoneLogOnly modunda çalışır.
 */
const WorkOrderEmailTab = ({ workOrderId, consumerId, getTasksRequest }) => {
  const classes = useStyles();
  const { t } = useTranslation(['mailorbit_workorder', 'mailorbit']);
  const [view, setView] = useState('email'); // 'email' | 'phonelog'
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState(null);
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorKey(null);
    getConversationsByExternalLink(EXTERNAL_SYSTEM, String(workOrderId))
      .then(res => {
        if (cancelled) return;
        const list = (res && res.items) || [];
        setItems(list);
        setActiveId(list.length ? list[0].conversationId : null);
      })
      .catch(e => {
        if (!cancelled) setErrorKey(getMailOrbitErrorKey(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workOrderId]);

  const chipClass = selected => `${classes.chip} ${selected ? classes.chipSelected : ''}`;

  const renderEmail = () => {
    if (loading) {
      return (
        <Box className={classes.center}>
          <CircularProgress />
        </Box>
      );
    }
    if (errorKey) {
      return (
        <Box className={classes.center}>
          <Alert severity="error">{t(`mailorbit:${errorKey}`)}</Alert>
        </Box>
      );
    }
    if (!activeId) {
      return (
        <Box className={classes.center}>
          <MailOutlineIcon style={{ fontSize: 48, opacity: 0.4 }} />
          <Typography variant="body1">{t('mailorbit_workorder:no_linked_email')}</Typography>
        </Box>
      );
    }
    return (
      <>
        {items.length > 1 && (
          <Box className={classes.toolbar}>
            {items.map(it => (
              <Chip
                key={it.conversationId}
                label={it.caseReference || it.conversationId}
                onClick={() => setActiveId(it.conversationId)}
                className={chipClass(it.conversationId === activeId)}
              />
            ))}
          </Box>
        )}
        <ConversationView key={activeId} conversationId={activeId} />
      </>
    );
  };

  return (
    <Box className={classes.root}>
      <BackToList />
      {consumerId && (
        <Box className={classes.toolbar}>
          <Chip
            label={t('mailorbit_workorder:tab_email')}
            onClick={() => setView('email')}
            className={chipClass(view === 'email')}
          />
          <Chip
            label={t('mailorbit_workorder:tab_phone_log')}
            onClick={() => setView('phonelog')}
            className={chipClass(view === 'phonelog')}
          />
        </Box>
      )}
      {view === 'phonelog' && consumerId ? (
        <Box style={{ flex: 1, minHeight: 0 }}>
          <OutlookStyleEmailView
            phoneLogOnly
            consumerId={consumerId}
            workOrderId={workOrderId}
            onClose={null}
            getTasksRequest={getTasksRequest}
            />
        </Box>
      ) : (
        renderEmail()
      )}
    </Box>
  );
};

WorkOrderEmailTab.propTypes = {
  workOrderId: PropTypes.number.isRequired,
  consumerId: PropTypes.any,
  getTasksRequest: PropTypes.func
};

export default WorkOrderEmailTab;
